import { Box, Button, Tooltip, Typography, alpha, useTheme } from "@mui/material";
import PropTypes from "prop-types";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";

const SoDashBoardFilter = ({
  filters,
  selectedFilters,
  onToggleFilter,
  getCount,
  onExport,
  onUpload,
}) => {
  const theme = useTheme();

  const filterSx = (selected) => ({
    height: 38,
    px: 2,
    display: "inline-flex",
    alignItems: "center",
    gap: 1,
    borderRadius: 999,
    border: "1px solid",
    borderColor: selected ? "primary.main" : "grey.300",
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.08)
      : "common.white",
    cursor: "pointer",
    transition: "all 0.15s ease",
    "&:hover": {
      borderColor: selected ? "primary.main" : "grey.400",
    },
  });

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 4,
        border: "1px solid",
        borderLeft: 0,
        borderColor: "grey.300",
      }}
    >

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
        {filters.map((filter) => {
          const selected = selectedFilters === filter.key;
          const Icon = filter.icon;

          const iconEl = (
            <Icon
              sx={{
                fontSize: 16,
                color: selected ? "primary.main" : "grey.A1000",
              }}
            />
          );

          return (
            <Box
              key={filter.key}
              component="button"
              type="button"
              onClick={() => onToggleFilter(filter.key)}
              sx={filterSx(selected)}
            >
              {filter.tooltip ? (
                <Tooltip title={filter.tooltip} arrow>
                  {iconEl}
                </Tooltip>
              ) : (
                iconEl
              )}
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: selected ? 600 : 400,
                  color: selected ? "primary.main" : "grey.A1000",
                  whiteSpace: "nowrap",
                }}
              >
                {filter.label}
              </Typography>
              <Box
                component="span"
                sx={{
                  minWidth: 20,
                  height: 18,
                  px: 0.75,
                  borderRadius: 9,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 11,
                  fontWeight: 700,
                  color: selected ? "primary.main" : "text.secondary",
                  backgroundColor: selected
                    ? alpha(theme.palette.primary.main, 0.15)
                    : "grey.200",
                }}
              >
                {getCount(filter.countKey)}
              </Box>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", gap: 1.5 }}>
        <Button
          onClick={onExport}
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
          sx={{
            height: 38,
            px: 3,
            border: "1px solid",
            borderColor: "grey.300",
            color: "primary.main",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "common.white",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              borderColor: "grey.400",
            },
          }}
        >
          Export
        </Button>

        <Button
          onClick={onUpload}
          startIcon={<FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
          sx={{
            height: 38,
            px: 4,
            border: "1px solid",
            borderColor: "grey.300",
            color: "primary.main",
            fontSize: 14,
            fontWeight: 600,
            textTransform: "none",
            backgroundColor: "common.white",
            "&:hover": {
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
              borderColor: "grey.400",
            },
          }}
        >
          Upload data
        </Button>
      </Box>
    </Box>
  );
};

SoDashBoardFilter.propTypes = {
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      icon: PropTypes.elementType.isRequired,
      countKey: PropTypes.string,
      tooltip: PropTypes.string,
    }),
  ).isRequired,
  selectedFilters: PropTypes.string.isRequired,
  onToggleFilter: PropTypes.func.isRequired,
  getCount: PropTypes.func.isRequired,
  onExport: PropTypes.func.isRequired,
  onUpload: PropTypes.func,
};

export default SoDashBoardFilter;